/* eslint-disable style/quote-props */
import type { SyntheticEvent } from 'react';
import type { CompanyOption } from '@shared/components/Modals/FollowCompanyModal.types';
import type { FilterOptionsState } from '@mui/material';
import { Add } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, CircularProgress, Fade, TextField } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { fetchCompanies } from '../../../scenes/company/company-search/api/api';
import CompanyLogo from '@shared/components/CompanyLogo';
import '../../../scss/components/_company-search-autocomplete.scss';

interface AddNewOption extends CompanyOption {
  isAddNew: true;
}

function isAddNewOption(
  option: CompanyOption,
): option is AddNewOption {
  return 'isAddNew' in option
    && (option as AddNewOption).isAddNew === true;
}

interface CompanySearchAutocompleteProps {
  /** Current selected company */
  value: CompanyOption | null;
  /** Callback when selection changes */
  onChange: (company: CompanyOption | null) => void;
  /** Label for the input field */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Help text shown below the input */
  helperText?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Test ID for testing purposes */
  'data-testid'?: string;
  /** Whether to allow creating new companies (freeSolo) */
  allowNewCompany?: boolean;
  /** Minimum characters required before searching */
  minSearchLength?: number;
  /** Callback when "Add Company" is clicked (receives the current search term) */
  onAddCompanyClick?: (searchTerm: string) => void;
}

export function CompanySearchAutocomplete({
  value,
  onChange,
  label = 'Company',
  placeholder = 'Type or select a company',
  helperText,
  disabled = false,
  'data-testid': testId,
  allowNewCompany = true,
  minSearchLength = 2,
  onAddCompanyClick,
}: CompanySearchAutocompleteProps) {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(value?.label || '');
  const [_hasSearched, setHasSearched] = useState(false);
  const [showNoOptions, setShowNoOptions] = useState(false);

  // Debounce the search input to avoid too many API calls
  const [debouncedSearchInput] = useDebounce(searchInput, 300);

  // Search for companies when debounced input changes and meets minimum length
  useEffect(() => {
    if (debouncedSearchInput.length >= minSearchLength) {
      setLoading(true);
      setHasSearched(false);
      setShowNoOptions(false);

      fetchCompanies({
        searchTerm: debouncedSearchInput,
        limit: 50,
        offset: 0,
      })
        .then((response) => {
          const companyOptions: CompanyOption[] = response.results.map(c => ({
            id: c.id,
            label: c.companyName,
            url: c.url,
            logo: `https://kandir-images.s3.eu-west-2.amazonaws.com/Company Logos/${encodeURIComponent(
              c.companyName,
            )}.jpeg`,
          }));
          setCompanies(companyOptions);
          setHasSearched(true);

          if (companyOptions.length === 0) {
            setTimeout(() => setShowNoOptions(true), 200);
          }
        })
        .catch((error) => {
          console.error('[CompanySearchAutocomplete] Error fetching companies:', error);
          setCompanies([]);
          setHasSearched(true);
          setTimeout(() => setShowNoOptions(true), 200);
        })
        .finally(() => {
          setLoading(false);
        });
    }
    else {
      setCompanies([]);
      setHasSearched(false);
      setShowNoOptions(false);
    }
  }, [debouncedSearchInput, minSearchLength]);

  // Sync search input with value prop changes
  useEffect(() => {
    setSearchInput(value?.label || '');
  }, [value]);

  const handleInputChange = useCallback((event: SyntheticEvent<Element, Event>, newInputValue: string) => {
    setSearchInput(newInputValue);
  }, []);

  const handleSelectionChange = useCallback((event: SyntheticEvent<Element, Event>, newValue: CompanyOption | string | null) => {
    if (!newValue) {
      onChange(null);
    }
    else if (typeof newValue === 'string') {
      if (allowNewCompany) {
        onChange({
          id: undefined,
          label: newValue,
          logo: undefined,
        });
      }
    }
    else if (isAddNewOption(newValue)) {
      onAddCompanyClick?.(newValue.label);
    }
    else {
      onChange(newValue);
      setSearchInput('');
    }
  }, [onChange, allowNewCompany, onAddCompanyClick]);

  const filterOptionsWithAddNew = useCallback(
    (
      options: CompanyOption[],
      params: FilterOptionsState<CompanyOption>,
    ): CompanyOption[] => {
      if (!onAddCompanyClick) {
        return options;
      }

      const trimmedInput = params.inputValue.trim();
      if (trimmedInput.length < minSearchLength) {
        return options;
      }

      const hasExactMatch = options.some(
        opt => opt.label.trim().toLowerCase()
          === trimmedInput.toLowerCase(),
      );
      if (hasExactMatch) {
        return options;
      }

      const addNewOption: AddNewOption = {
        label: trimmedInput,
        isAddNew: true,
      };

      return [...options, addNewOption];
    },
    [onAddCompanyClick, minSearchLength],
  );

  const renderOption = useCallback((props: React.HTMLAttributes<HTMLLIElement>, option: CompanyOption) => {
    if (isAddNewOption(option)) {
      return (
        <li
          {...props}
          key="add-new-company"
          className={`${props.className || ''} company-search-add-new-option`}
        >
          <Add fontSize="small" />
          <span>
            Add &ldquo;
            {option.label}
            &rdquo; to database
          </span>
        </li>
      );
    }

    return (
      <li {...props} key={option.id || option.label}>
        <div className="company-logo-container company-logo-container--option">
          <CompanyLogo
            companyName={option.label}
            companyId={option.id}
            companyUrl={option.url}
            sx={{ width: '100%', height: '100%' }}
            alt={option.label}
          />
        </div>
        <span>{option.label}</span>
      </li>
    );
  }, []);

  const renderInput = useCallback((params: any) => (
    <TextField
      {...params}
      label={label}
      placeholder={placeholder}
      data-testid={testId}
      fullWidth
      helperText={helperText}
      disabled={disabled}
      slotProps={{
        input: {
          ...params.InputProps,
          startAdornment: value ? (
            <div className="company-logo-container company-logo-container--input">
              <CompanyLogo
                companyName={value.label}
                companyId={value.id}
                companyUrl={value.url}
                sx={{ width: '100%', height: '100%' }}
                alt={value.label}
              />
            </div>
          ) : null,
          endAdornment: (
            <>
              {loading ? <CircularProgress color="inherit" size={20} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        },
      }}
    />
  ), [label, placeholder, testId, helperText, disabled, loading, value]);

  // Memoize the no options text to prevent re-renders and flickering
  const noOptionsContent = useMemo(() => {
    if (debouncedSearchInput.length < minSearchLength) {
      return `Type at least ${minSearchLength} characters to search`;
    }

    // Don't show the message while loading or if we haven't waited long enough
    if (loading || !showNoOptions) {
      return '';
    }

    return (
      <Fade in timeout={300}>
        <div className="company-search-no-options">
          <div className="company-search-no-options__title">
            No companies found for &ldquo;
            {debouncedSearchInput}
            &rdquo;
          </div>
          <div className="company-search-no-options__description">
            Kandir's company database was seeded with the Russell 3000 list of public companies. If you don't see one you're looking for, feel free to create it!
          </div>
        </div>
      </Fade>
    );
  }, [debouncedSearchInput, minSearchLength, loading, showNoOptions]);

  return (
    <div className="company-search">
      <Autocomplete
        className="company-search-autocomplete"
        freeSolo={allowNewCompany}
        clearOnEscape
        loading={loading}
        filterOptions={filterOptionsWithAddNew}
        options={companies}
        inputValue={searchInput}
        onInputChange={handleInputChange}
        value={value}
        onChange={handleSelectionChange}
        popupIcon={null}
        clearIcon={<CloseIcon fontSize="small" />}
        getOptionLabel={option =>
          typeof option === 'string' ? option : option.label}
        isOptionEqualToValue={(option, val) => {
          if (isAddNewOption(option)) {
            return false;
          }
          return option.id === val.id && option.label === val.label;
        }}
        renderInput={renderInput}
        renderOption={renderOption}
        disabled={disabled}
        noOptionsText={noOptionsContent}
      />
    </div>
  );
}

export default CompanySearchAutocomplete;
